import { css, cx } from "@emotion/css";
import { Link, useLocation } from "react-router-dom";
import { FiHome, FiCalendar, FiHelpCircle, FiUser } from "react-icons/fi";
import LogoMark from "../../ui/logo-mark/logo-mark";
import { useAuth } from "../../../features/auth/hooks/use-auth";

const topNavWrapper = css({
  position: 'sticky',
  top: 0,
  zIndex: 50,
  background: 'var(--primary-color)',
  borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  display: 'flex',
});

const topNavInner = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0.75rem var(--spacing-lg)',
  width: '100%',
});

const logoLink = css({
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  transform: 'scale(0.85)',
  ':focus-visible': {
    outline: '2px solid var(--pc-500)',
    outlineOffset: '2px',
    borderRadius: 4,
  },
});

const bottomNavWrapper = css({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 50,
  background: 'var(--primary-color)',
  borderTop: '1px solid rgba(124, 58, 237, 0.1)',
  boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)',
  display: 'flex',
});

const navContainer = css({
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  padding: '0.5rem 0',
});

const navItem = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.25rem',
  padding: '0.5rem 1rem',
  textDecoration: 'none',
  color: 'var(--g-600)',
  transition: 'color 0.2s ease',
  minWidth: '70px',
  ':hover': {
    color: 'var(--pc-600)',
  },
  ':focus-visible': {
    outline: '2px solid var(--pc-500)',
    outlineOffset: '2px',
    borderRadius: '8px',
  },
});

const activeNavItem = css({
  color: 'var(--pc-600)',
});

const iconWrapper = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const navLabel = css({
  fontSize: '0.75rem',
  fontWeight: 500,
  textAlign: 'center',
  lineHeight: 1.2,
});

interface NavItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  match?: string | RegExp;
}

interface NavbarMobileProps extends React.HTMLAttributes<HTMLElement> {
  className?: string;
  ref?: React.Ref<HTMLElement>;
}

export function NavbarMobile({ className, ref, ...props }: NavbarMobileProps) {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();

  const navItems: NavItem[] = [
    { 
      label: 'Home', 
      to: '/', 
      icon: FiHome,
      match: /^\/$/ 
    },
    { 
      label: 'Eventos', 
      to: '/#eventos', 
      icon: FiCalendar,
      match: /^\/#eventos/ 
    },
    { 
      label: 'Ajuda', 
      to: '/contato', 
      icon: FiHelpCircle,
      match: /^\/contato/ 
    },
    { 
      label: 'Minha Conta', 
      to: isAuthenticated ? '/perfil' : '/entrar', 
      icon: FiUser,
      match: isAuthenticated ? /^\/perfil/ : /^\/entrar/ 
    },
  ];

  const isActive = (item: NavItem) =>
    item.match instanceof RegExp
      ? item.match.test(pathname)
      : pathname === item.match;

  return (
    <>
      {/* Top navigation with logo */}
      <header ref={ref} className={cx(topNavWrapper, className)} {...props}>
        <div className={topNavInner}>
          <Link to="/" className={logoLink} aria-label="Página inicial">
            <LogoMark />
          </Link>
        </div>
      </header>

      {/* Bottom navigation */}
      <nav className={cx(bottomNavWrapper, className)} aria-label="Navegação móvel principal" {...props}>
        <div className={navContainer}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            
            return (
              <Link
                key={item.to}
                to={item.to}
                className={active ? `${navItem} ${activeNavItem}` : navItem}
                aria-current={active ? 'page' : undefined}
              >
                <div className={iconWrapper}>
                  <Icon size={22} />
                </div>
                <span className={navLabel}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}

export default NavbarMobile;
